import { DateSchema, array, boolean, date, number, object, ref, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import i18n from 'locals/i18n';
import { CustomFee, DefaultValuesTypes, Event, EventDateType } from './types';
import { convertUTCToDate } from 'utils/utils';
import { EVENT_MODE_NORMAL, ZERO_TIME } from 'utils/constants';
import { EventMode } from 'utils/types';

export const defaultValues: DefaultValuesTypes = {
	name: '',
	venue: '',
	location: '',
	description: '',
	selling_time: null,
	dates: [],
	custom_fees: [],
	timezone: '',
	gate_time: '',
	postal_code: '',
	isMultiDay: false,
	ticketing_mode: 'dynamic',
	custom_email_message: '',
	support: '',
	mode: EVENT_MODE_NORMAL,
	quantity: '0',
	approval_required: false,
	protect_with_password: false,
	password: '',
	confirm_password: '',
	change_password: false,
	want_to_change_password: false,
	remove_password: false,
	on_sale: true,
	hide_event: false,
	hidden_until: null,
};

export const getFormDetails = (eventMode: EventMode) => {
	const sharedDetails = {
		name: string().required(i18n.t('editEvent-requiredName')),
		location: string().required(i18n.t('editEvent-requiredLocation')),
		timezone: string().required(i18n.t('editEvent-requiredTimezone')),
		custom_fees: array(
			object({
				name: string().required('Required'),
				amount: number()
					.required()
					.when('type', {
						is: 'percentage',
						then: number().min(0, 'Must be a number between 0 and 100').max(100, 'Must be a number between 0 and 100'),
					}),
			})
		),
		dates: array(
			object({
				start_date: date().required(),
				end_date: date()
					.required()
					.when('start_date', (start_date: Date, schema: DateSchema) =>
						schema.min(start_date, i18n.t('error-endDate'))
					),
			})
		),
		change_password: boolean(),
		want_to_change_password: boolean(),
		password: string().when('want_to_change_password', {
			is: true,
			then: (schema) =>
				schema.required(i18n.t('Password is required')).min(8, i18n.t('Password must be at least 8 characters')),
			otherwise: (schema) => schema.notRequired(),
		}),
		confirm_password: string().when(['want_to_change_password', 'password'], {
			is: (wantToChange: boolean, password: string) => wantToChange || !!password,
			then: (schema) =>
				schema
					.required(i18n.t('Please confirm your password'))
					.oneOf([ref('password')], i18n.t('Passwords must match')),
			otherwise: (schema) => schema.notRequired(),
		}),
		on_sale: boolean(),
		hide_event: boolean(),
		hidden_until: date()
			.nullable()
			.when('hide_event', {
				is: true,
				then: (schema) => schema.required(i18n.t('Hidden until date is required')),
				otherwise: (schema) => schema.notRequired(),
			}),
	};

	const normalSchema = object().shape({
		...sharedDetails,
		postal_code: string().required(i18n.t('editEvent-requiredZipcode')),
	});

	const registrationSchema = object().shape({
		...sharedDetails,
		quantity: string().required(i18n.t('editEvent-requiredQuantity')),
	});

	if (eventMode === EVENT_MODE_NORMAL) {
		return yupResolver(normalSchema);
	} else {
		return yupResolver(registrationSchema);
	}
};

export const handleInitialValues = (event: Event) => {
	const dates = event.days.map(({ start_date, end_date, id }: EventDateType) => ({
		start_date: new Date(convertUTCToDate(start_date, event?.time_zone)),
		end_date: new Date(convertUTCToDate(end_date, event?.time_zone)),
		id,
	}));

	const selling_time =
		event.selling_time === ZERO_TIME ? event.selling_time : convertUTCToDate(event.selling_time, event.time_zone);

	const custom_fees = event.custom_fees?.map(({ ID, name, amount, type, taxable, general }: CustomFee) => ({
		ID,
		name,
		amount,
		type,
		taxable,
		general,
	}));

	const hidden_until = event.hidden_until ? new Date(convertUTCToDate(event.hidden_until, event.time_zone)) : null;

	const allValues: DefaultValuesTypes = {
		name: event.name,
		location: event.street_address,
		venue: event.venue,
		description: event.description,
		selling_time,
		dates: dates,
		timezone: event.time_zone,
		gate_time: event.gate_time ? event.gate_time : '',
		postal_code: event.postal_code,
		isMultiDay: event.days.length > 1,
		ticketing_mode: event.ticketing_mode,
		custom_fees: custom_fees || [],
		custom_email_message: event.custom_email_message,
		support: event.support_contact?.[0]?.contact || '',
		mode: event.mode,
		quantity: `${event.quantity}`,
		approval_required: event.approval_required,
		protect_with_password: event.has_password || false,
		password: '',
		confirm_password: '',
		change_password: event.has_password || false,
		want_to_change_password: false,
		remove_password: false,
		on_sale: event.on_sale ?? true,
		hide_event: !!event.hidden_until,
		hidden_until: hidden_until,
	};
	return allValues;
};
